import { ActionIcon, Group, Text, Tooltip } from '@mantine/core';
import { modals } from '@mantine/modals';
import { IconArrowBackUp, IconTrash, IconX } from '@tabler/icons-react';
import type { TaskActionType } from 'app/Private/Clients/Detail/TaskTab/types/task-action-type';
import { useProfile } from 'init/use-enforce-profile';
import { tKeys, useTranslation } from 'locales/index';
import { useDeleteTask } from 'shared/hooks/use-delete-task';
import type { Task } from 'shared/hooks/use-load-tasks';
import { useUpdateTaskStatus } from 'shared/hooks/use-update-task-status';

type TaskActionsProps = {
  task: Task;
};

export const TaskActions = ({ task }: TaskActionsProps) => {
  const { t } = useTranslation();
  const profile = useProfile();

  const { updateTaskStatus, isUpdateTaskStatusLoading } = useUpdateTaskStatus();
  const { deleteTask } = useDeleteTask();

  const handleStatusAction = (e: React.MouseEvent, actionType: TaskActionType) => {
    e.stopPropagation();
    updateTaskStatus({ task_ids: [String(task.id)], action_type: actionType });
  };

  const handleDeleteTask = (e: React.MouseEvent) => {
    e.stopPropagation();
    modals.openConfirmModal({
      title: t(tKeys.CONFIRM),
      centered: true,
      children: <Text>{t(tKeys.AYS.DELETE_TASK)}</Text>,
      labels: { confirm: t(tKeys.DELETE), cancel: t(tKeys.CANCEL) },
      confirmProps: { color: 'red' },
      onConfirm: () => deleteTask({ id: Number(task.id) }),
    });
  };

  return (
    <Group gap={8}>
      {!task.skipped && !task.completed && (
        <Tooltip label={t(tKeys.TASK.ACTIONS.SKIP)}>
          <ActionIcon
            radius={8}
            size="input-xs"
            color="gray"
            variant="light"
            onClick={e => handleStatusAction(e, 'skip')}
            loading={isUpdateTaskStatusLoading}
            disabled={isUpdateTaskStatusLoading}
          >
            <IconX size={18} stroke={1.5} />
          </ActionIcon>
        </Tooltip>
      )}
      {task.skipped && (
        <ActionIcon
          radius={8}
          size="input-xs"
          color="purple"
          variant="light"
          onClick={e => handleStatusAction(e, 'revert')}
          loading={isUpdateTaskStatusLoading}
          disabled={isUpdateTaskStatusLoading}
        >
          <IconArrowBackUp size={18} stroke={1.5} />
        </ActionIcon>
      )}
      {profile.isAdmin && (
        <ActionIcon
          radius={8}
          color="red"
          size="input-xs"
          variant="light"
          onClick={handleDeleteTask}
        >
          <IconTrash size={18} stroke={1.5} />
        </ActionIcon>
      )}
    </Group>
  );
};
